import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Headset, LogOut, MapPin, MessageCircle, Navigation, Phone, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PickupMap from "../../components/PickupMap";
import ChangePassword from "../../components/ChangePassword";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";

const LEAD_BADGE = { new: "bg-sky-500/15 text-sky-400", followup: "bg-amber-500/15 text-amber-400", agreed: "bg-emerald-500/15 text-emerald-400" };
const TASK_BADGE = {
  scheduled: "bg-sky-500/15 text-sky-400", reached: "bg-violet-500/15 text-violet-400",
  pickup_done: "bg-emerald-500/15 text-emerald-400", bill_shared: "bg-amber-500/15 text-amber-400",
  amount_taken: "bg-teal-500/15 text-teal-400", submitted_office: "bg-slate-500/15 text-slate-400",
};

export default function CrmPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [leads, setLeads] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [boys, setBoys] = useState([]);
  const [mapPoints, setMapPoints] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", country: "USA", approx_kg: "", special_items: "" });
  const [followLead, setFollowLead] = useState(null);
  const [openLogs, setOpenLogs] = useState(null);
  const [followForm, setFollowForm] = useState({ note: "", outcome: "" });
  const [schedLead, setSchedLead] = useState(null);
  const [schedForm, setSchedForm] = useState({ pickup_boy_id: "", address: "", latitude: "", longitude: "", pincode: "" });

  const loadLeads = useCallback(() => api.get("/crm/leads").then((r) => setLeads(r.data)).catch(() => {}), []);
  const loadPickups = useCallback(() => api.get("/crm/pickups").then((r) => setPickups(r.data)).catch(() => {}), []);
  const loadMap = useCallback(() => api.get("/crm/map").then((r) => setMapPoints(r.data)).catch(() => {}), []);

  useEffect(() => {
    loadLeads(); loadPickups();
    api.get("/crm/pickup-boys").then((r) => setBoys(r.data)).catch(() => {});
    api.get("/rates/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, [loadLeads, loadPickups]);
  useEffect(() => {
    if (tab !== "map") return undefined;
    loadMap();
    const t = setInterval(loadMap, 15000);
    return () => clearInterval(t);
  }, [tab, loadMap]);

  const createLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/crm/leads", { ...form, approx_kg: parseFloat(form.approx_kg) });
      toast.success("Enquiry registered");
      setForm({ name: "", phone: "", country: "USA", approx_kg: "", special_items: "" });
      loadLeads();
    } catch (err) { toast.error(apiError(err)); }
  };

  const shareQuote = async (lead) => {
    try {
      const r = await api.get(`/crm/leads/${lead.id}/quote`);
      if (r.data.price) toast.success(`Best price ₹${Number(r.data.price).toLocaleString("en-IN")} via ${r.data.courier} — opening WhatsApp`);
      else toast.info("Price on request — WhatsApp message opened");
      window.open(r.data.wa_link, "_blank");
      loadLeads();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveFollowup = async () => {
    if (!followForm.note.trim()) { toast.error("Enter a follow-up note"); return; }
    try {
      await api.post(`/crm/leads/${followLead.id}/followups`, followForm);
      toast.success("Follow-up logged");
      setFollowLead(null); setFollowForm({ note: "", outcome: "" });
      loadLeads();
    } catch (err) { toast.error(apiError(err)); }
  };

  const schedulePickup = async () => {
    if (!schedForm.pickup_boy_id) { toast.error("Select a pickup boy"); return; }
    if (!schedForm.address.trim()) { toast.error("Enter pickup address"); return; }
    const lat = parseFloat(schedForm.latitude); const lng = parseFloat(schedForm.longitude);
    if (isNaN(lat) || isNaN(lng)) { toast.error("Enter valid latitude & longitude"); return; }
    try {
      const r = await api.post(`/crm/leads/${schedLead.id}/schedule`, { ...schedForm, latitude: lat, longitude: lng });
      toast.success("Pickup scheduled");
      if (r.data?.notify?.wa_link) {
        toast.success("Send pickup confirmation to customer", { duration: 12000,
          action: { label: "WhatsApp", onClick: () => notifyCustomer(r.data) } });
      }
      setSchedLead(null);
      setSchedForm({ pickup_boy_id: "", address: "", latitude: "", longitude: "", pincode: "" });
      loadLeads(); loadPickups();
    } catch (err) { toast.error(apiError(err)); }
  };

  const notifyCustomer = async (t) => {
    const link = t.notify?.wa_link;
    if (!link) { toast.error("Customer phone unavailable for WhatsApp"); return; }
    window.open(link, "_blank");
    try { await api.post(`/crm/tasks/${t.id}/notify-sent`); loadPickups(); } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-adminbg text-slate-200" data-testid="crm-panel">
      <header className="sticky top-0 z-40 border-b border-adminborder bg-admincard/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-saffron text-white"><Headset className="h-5 w-5" /></span>
            <div>
              <p className="font-heading text-sm font-extrabold text-white">INDIAFAST <span className="text-saffron">CRM Desk</span></p>
              <p className="text-[11px] text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ChangePassword testid="crm-change-password-btn"
                            triggerClass="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-white" />
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="crm-logout-btn"
                    className="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-white">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-2 px-4 pb-3">
          {[["enquiries", "Enquiries"], ["pickups", "Pickup Tracking"], ["map", "Live Map"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`crm-tab-${k}`}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${tab === k ? "bg-saffron text-white" : "border border-adminborder text-slate-400 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6">
        {tab === "enquiries" && (
          <>
            <div className={`${card} p-5`}>
              <h3 className="mb-3 text-sm font-bold text-white">Register Enquiry</h3>
              <form onSubmit={createLead} className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                <input required className={darkInput} data-testid="lead-name" placeholder="Customer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required className={darkInput} data-testid="lead-phone" placeholder="Phone (10-digit)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <select className={darkInput} data-testid="lead-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
                  {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input required type="number" step="0.1" min="0.1" className={darkInput} data-testid="lead-kg" placeholder="Approx KG" value={form.approx_kg} onChange={(e) => setForm({ ...form, approx_kg: e.target.value })} />
                <input className={`${darkInput} lg:col-span-1`} data-testid="lead-items" placeholder="Special items" value={form.special_items} onChange={(e) => setForm({ ...form, special_items: e.target.value })} />
                <button type="submit" data-testid="lead-save-btn" className="flex items-center justify-center gap-1.5 rounded-lg bg-saffron px-4 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">
                  <Plus className="h-4 w-4" /> Save
                </button>
              </form>
            </div>

            <div className={`${card} overflow-x-auto`}>
              <table className="w-full text-sm" data-testid="crm-leads-table">
                <thead>
                  <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3">Customer</th><th className="px-5 py-3">Phone</th><th className="px-5 py-3">Country</th>
                    <th className="px-5 py-3">KG</th><th className="px-5 py-3">Quoted</th><th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Follow-ups</th><th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <Fragment key={l.id}>
                    <tr className="border-b border-adminborder/60 last:border-0 hover:bg-white/5" data-testid={`lead-row-${l.id}`}>
                      <td className="px-5 py-3 font-bold text-white">{l.name}<p className="text-[10px] font-normal text-slate-500">{l.special_items || ""}</p></td>
                      <td className="px-5 py-3 font-mono text-xs">{l.phone}</td>
                      <td className="px-5 py-3">{l.country}</td>
                      <td className="px-5 py-3">{l.approx_kg}</td>
                      <td className="px-5 py-3 font-bold text-emerald-400">{l.quoted_price ? `₹${Number(l.quoted_price).toLocaleString("en-IN")}` : "—"}</td>
                      <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${LEAD_BADGE[l.status] || LEAD_BADGE.new}`}>{l.status}</span></td>
                      <td className="px-5 py-3 text-xs">
                        <button onClick={() => setOpenLogs(openLogs === l.id ? null : l.id)} data-testid={`lead-logs-toggle-${l.id}`}
                                className={`rounded-lg border px-2.5 py-1 font-bold transition-colors ${openLogs === l.id ? "border-saffron text-saffron" : "border-adminborder text-slate-300 hover:border-saffron hover:text-saffron"}`}>
                          {l.followups?.length || 0}/3 logs {openLogs === l.id ? "▲" : "▼"}
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <button onClick={() => shareQuote(l)} data-testid={`lead-quote-${l.id}`} title="Quote & share price on WhatsApp"
                                  className="rounded-lg bg-emerald-500/15 p-2 text-emerald-400 hover:bg-emerald-500/25"><MessageCircle className="h-3.5 w-3.5" /></button>
                          <button onClick={() => { setFollowLead(l); setFollowForm({ note: "", outcome: "" }); }} data-testid={`lead-followup-${l.id}`} title={(l.followups?.length || 0) >= 3 ? "3/3 follow-ups completed" : "Log follow-up"}
                                  disabled={(l.followups?.length || 0) >= 3}
                                  className="rounded-lg bg-amber-500/15 p-2 text-amber-400 hover:bg-amber-500/25 disabled:cursor-not-allowed disabled:opacity-30"><Phone className="h-3.5 w-3.5" /></button>
                          {l.status !== "agreed" && (
                            <button onClick={() => { setSchedLead(l); setSchedForm({ pickup_boy_id: "", address: "", latitude: "", longitude: "", pincode: "" }); }} data-testid={`lead-schedule-${l.id}`} title="Raise pickup"
                                    className="rounded-lg bg-saffron/15 p-2 text-saffron hover:bg-saffron/25"><Navigation className="h-3.5 w-3.5" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {openLogs === l.id && (
                      <tr className="border-b border-adminborder/60 bg-adminbg/60">
                        <td colSpan={8} className="px-5 py-3" data-testid={`lead-logs-panel-${l.id}`}>
                          {(l.followups || []).length ? (
                            <div className="space-y-1.5">
                              {l.followups.map((f, i) => (
                                <p key={i} className="text-xs text-slate-400">
                                  <b className="text-slate-200">Follow-up #{i + 1}</b> · {f.by} · {new Date(f.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} — {f.note}{f.outcome ? <span className="text-amber-400"> · Outcome: {f.outcome}</span> : ""}
                                </p>
                              ))}
                            </div>
                          ) : <p className="text-xs text-slate-500">No follow-ups logged yet.</p>}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                  {!leads.length && <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-500">No enquiries yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "pickups" && (
          <div className="space-y-4" data-testid="crm-pickups">
            {pickups.map((t) => (
              <div key={t.id} className={`${card} p-5`} data-testid={`crm-pickup-${t.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-white">{t.customer_name} <span className="ml-2 font-mono text-xs font-normal text-slate-400">{t.phone}</span></p>
                    <p className="mt-0.5 text-xs text-slate-400">{t.address} · {t.country} · ~{t.approx_kg} kg{t.special_items ? ` · ${t.special_items}` : ""}</p>
                    <p className="mt-1 text-xs text-slate-500">Pickup boy: <b className="text-slate-300">{t.pickup_boy_name}</b> · Raised by {t.created_by_name}{t.shipment_awb ? ` · AWB ${t.shipment_awb}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.notify?.wa_link && !t.notify?.sent_at && (
                      <button onClick={() => notifyCustomer(t)} data-testid={`crm-notify-${t.id}`} title="Send pickup confirmation on WhatsApp"
                              className="flex items-center gap-1 rounded-lg bg-emerald-500/15 px-3 py-1.5 text-[11px] font-bold text-emerald-400 hover:bg-emerald-500/25">
                        <MessageCircle className="h-3.5 w-3.5" /> Notify customer
                      </button>
                    )}
                    {t.notify?.sent_at && <span className="text-[10px] font-bold text-emerald-400" data-testid={`crm-notified-${t.id}`}>✓ customer notified</span>}
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ${TASK_BADGE[t.status] || TASK_BADGE.scheduled}`}>{t.status?.replace("_", " ")}</span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(t.status_log || []).map((s, i) => (
                    <span key={i} className="rounded-full border border-adminborder px-2.5 py-1 text-[10px] text-slate-400">
                      {s.status?.replace("_", " ")} · {new Date(s.at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {!pickups.length && <div className={`${card} py-12 text-center text-sm text-slate-500`}>No pickups scheduled yet.</div>}
          </div>
        )}

        {tab === "map" && (
          <div className="space-y-4" data-testid="crm-map-tab">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">Pickup boys on duty: <span className="text-saffron">{mapPoints.length}</span></h3>
              <p className="text-[11px] text-slate-500">Auto-refreshes every 15 seconds</p>
            </div>
            <PickupMap points={mapPoints} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {boys.map((b) => (
                <div key={b.id} className={`${card} flex items-center justify-between p-4`} data-testid={`crm-boy-${b.id}`}>
                  <div>
                    <p className="text-sm font-bold text-white">{b.name}</p>
                    <p className="text-[11px] text-slate-500">{b.location_at ? `Last seen ${new Date(b.location_at).toLocaleTimeString("en-IN")}` : "No location yet"}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${b.on_duty ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                    {b.on_duty ? "ON DUTY" : "OFF DUTY"}
                  </span>
                </div>
              ))}
              {!boys.length && <p className="text-sm text-slate-500">No pickup boy accounts yet — admin creates them.</p>}
            </div>
          </div>
        )}
      </main>

      {followLead && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="followup-modal">
          <div className={`${card} w-full max-w-lg p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">Follow-ups — {followLead.name}</h3>
            <div className="mt-3 space-y-2">
              {(followLead.followups || []).map((f, i) => (
                <div key={i} className="rounded-lg border border-adminborder p-3 text-xs">
                  <p className="font-bold text-slate-200">#{i + 1} · {f.by} · {new Date(f.at).toLocaleString("en-IN")}</p>
                  <p className="mt-1 text-slate-400">{f.note}{f.outcome ? ` — ${f.outcome}` : ""}</p>
                </div>
              ))}
              {!(followLead.followups || []).length && <p className="text-xs text-slate-500">No follow-ups logged yet.</p>}
            </div>
            {(followLead.followups || []).length < 3 ? (
              <div className="mt-4 space-y-3">
                <textarea className={darkInput} rows={2} data-testid="followup-note" placeholder="Follow-up note (what was discussed)…" value={followForm.note} onChange={(e) => setFollowForm({ ...followForm, note: e.target.value })} />
                <input className={darkInput} data-testid="followup-outcome" placeholder="Outcome (e.g. will confirm tomorrow)" value={followForm.outcome} onChange={(e) => setFollowForm({ ...followForm, outcome: e.target.value })} />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setFollowLead(null)} className="rounded-lg border border-adminborder px-4 py-2 text-xs font-bold text-slate-300">Close</button>
                  <button onClick={saveFollowup} data-testid="followup-save-btn" className="rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white">Log Follow-up</button>
                </div>
              </div>
            ) : (
              <p className="mt-4 rounded-lg bg-amber-500/10 p-3 text-xs font-semibold text-amber-400">Maximum 3 follow-ups completed for this enquiry.</p>
            )}
          </div>
        </div>
      )}

      {schedLead && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="schedule-modal">
          <div className={`${card} w-full max-w-lg p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">Raise Pickup — {schedLead.name}</h3>
            <p className="mt-1 text-xs text-slate-500">{schedLead.country} · ~{schedLead.approx_kg} kg{schedLead.quoted_price ? ` · Quoted ₹${Number(schedLead.quoted_price).toLocaleString("en-IN")}` : ""}</p>
            <div className="mt-4 space-y-3">
              <select className={darkInput} data-testid="schedule-boy" value={schedForm.pickup_boy_id} onChange={(e) => setSchedForm({ ...schedForm, pickup_boy_id: e.target.value })}>
                <option value="">Select pickup boy…</option>
                {boys.map((b) => <option key={b.id} value={b.id}>{b.name}{b.on_duty ? " (on duty)" : ""}</option>)}
              </select>
              <textarea className={darkInput} rows={2} data-testid="schedule-address" placeholder="Full pickup address" value={schedForm.address} onChange={(e) => setSchedForm({ ...schedForm, address: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input className={darkInput} data-testid="schedule-lat" placeholder="Latitude (e.g. 17.3850)" value={schedForm.latitude} onChange={(e) => setSchedForm({ ...schedForm, latitude: e.target.value })} />
                <input className={darkInput} data-testid="schedule-lng" placeholder="Longitude (e.g. 78.4867)" value={schedForm.longitude} onChange={(e) => setSchedForm({ ...schedForm, longitude: e.target.value })} />
              </div>
              <button type="button" data-testid="schedule-gps-btn"
                      onClick={() => navigator.geolocation?.getCurrentPosition(
                        (pos) => setSchedForm({ ...schedForm, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) }),
                        () => toast.error("Location permission denied"))}
                      className="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-1.5 text-[11px] font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                <MapPin className="h-3.5 w-3.5" /> Use my GPS coordinates
              </button>
              <div className="flex justify-end gap-2">
                <button onClick={() => setSchedLead(null)} className="rounded-lg border border-adminborder px-4 py-2 text-xs font-bold text-slate-300">Cancel</button>
                <button onClick={schedulePickup} data-testid="schedule-save-btn" className="rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white">Schedule Pickup</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
